import { useState, useCallback, useEffect, useRef } from 'react'

import { createAssistantMessage, createUserMessage, MESSAGE_STATUS, isUserMessage } from 'components/agent_hub_components'

/**
 * Custom hook for managing chat messages state
 * Provides functionality to add user messages, assistant messages, and check loading state
 */
export const useMessages = (initialMessages = []) => {
  const [messages, setMessages] = useState(initialMessages)
  const lastInitialMessagesRef = useRef(initialMessages)

  // Update messages when initialMessages change (for thread loading)
  useEffect(() => {
    // Only update if initialMessages has actually changed
    if (initialMessages !== lastInitialMessagesRef.current) {
      setMessages(initialMessages)
      lastInitialMessagesRef.current = initialMessages
    }
  }, [initialMessages])

  const addUserMessage = useCallback((content, status = MESSAGE_STATUS.SUCCESS) => {
    const userMessage = createUserMessage({ content, status })
    setMessages((prev) => [...prev, userMessage])
    return userMessage
  }, [])

  const addAssistantMessage = useCallback((content, status = MESSAGE_STATUS.SUCCESS, queryId, responseId = null) => {
    const assistantMessage = createAssistantMessage({ content, status, queryId, responseId })
    setMessages((prev) => [...prev, assistantMessage])
    return assistantMessage
  }, [])

  const clearMessages = useCallback(() => {
    setMessages([])
  }, [])

  // Check if the most recent message is from user (indicates loading state)
  const shouldShowLoading = useCallback(() => {
    const mostRecentMessage = messages[messages.length - 1]
    return isUserMessage(mostRecentMessage)
  }, [messages])

  return {
    messages,
    addUserMessage,
    addAssistantMessage,
    clearMessages,
    shouldShowLoading
  }
}
